import { useState, useEffect } from 'react';
import { useAuthContext } from '@/components/providers/AuthProvider';

export interface UserConnection {
  id: string;
  user: {
    id: string;
    name: string;
    username: string;
    avatar?: string;
    role: string;
  };
  createdAt: string;
}

export function useUserConnections() {
  const [followers, setFollowers] = useState<UserConnection[]>([]);
  const [following, setFollowing] = useState<UserConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthContext();

  const fetchConnections = async (userId: string, type: 'followers' | 'following') => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/users/connections?userId=${userId}&type=${type}`);
      if (!response.ok) {
        throw new Error('Failed to fetch connections');
      }

      const data = await response.json();
      
      if (type === 'followers') {
        setFollowers(data);
      } else {
        setFollowing(data);
      }
    } catch (err) {
      console.error(`Error fetching ${type}:`, err);
      setError(`Failed to load ${type}`);
    } finally {
      setLoading(false);
    }
  };

  const followUser = async (followingId: string) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/follow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_user_id: followingId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to follow user');
      }

      const result = await response.json();
      
      // Refresh following list
      if (user.id) {
        await fetchConnections(user.id, 'following');
      }

      return result;
    } catch (err) {
      console.error('Error following user:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to follow user');
    }
  };

  const unfollowUser = async (followingId: string) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/unfollow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_user_id: followingId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to unfollow user');
      }

      // Refresh following list
      if (user.id) {
        await fetchConnections(user.id, 'following');
      }

      return { success: true };
    } catch (err) {
      console.error('Error unfollowing user:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to unfollow user');
    }
  };

  const checkIfFollowing = (userId: string): boolean => {
    return following.some(connection => connection.user.id === userId);
  };

  const getFollowerCount = (): number => {
    return followers.length;
  };

  const getFollowingCount = (): number => {
    return following.length;
  };

  return {
    followers,
    following,
    loading,
    error,
    fetchConnections,
    followUser,
    unfollowUser,
    checkIfFollowing,
    getFollowerCount,
    getFollowingCount
  };
} 